import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

interface PermissionData {
  role?: string;
  allAccess?: boolean;
  pageAccess?: Record<string, boolean>;
  operations?: Record<string, Record<string, boolean>>;
}

export interface PermissionContextValue {
  loading: boolean;
  isBoss: boolean;
  hasPageAccess: (pageKey: string) => boolean;
  hasOperation: (pageKey: string, op: string) => boolean;
  refresh: () => Promise<void>;
}

export const PermissionContext = createContext<PermissionContextValue>({
  loading: true,
  isBoss: false,
  hasPageAccess: () => false,
  hasOperation: () => false,
  refresh: async () => {},
});

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PermissionData | null>(null);
  const retryCountRef = useRef(0);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setData(null);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/my-permissions');
      setData(response.data);
      retryCountRef.current = 0; // Reset on success
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;

      if (status === 401) {
        // Token invalid/expired — clear and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setData(null);
        window.location.href = '/login';
        return;
      }

      // Network or server error — retry up to MAX_RETRIES times
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        return fetchPermissions();
      }

      // Exhausted retries — default-deny (data stays null)
      console.error('Failed to fetch permissions after retries:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const isBoss = data?.role === 'boss' || data?.allAccess === true;

  const hasPageAccess = useCallback(
    (pageKey: string): boolean => {
      // Boss / allAccess = full access
      if (data?.allAccess || data?.role === 'boss') return true;

      // No data (failed fetch or not loaded) = deny
      if (!data) return false;

      const pageAccess = data.pageAccess;

      // Default-deny: new employees without a permission record (empty pageAccess) have no access
      // until boss configures them via Settings → Employees → Permissions.
      if (!pageAccess || Object.keys(pageAccess).length === 0) return false;

      // Explicitly true = allow; anything else (false / missing key) = deny
      return pageAccess[pageKey] === true;
    },
    [data]
  );

  const hasOperation = useCallback(
    (pageKey: string, op: string): boolean => {
      // Boss / allAccess = full access
      if (data?.allAccess || data?.role === 'boss') return true;

      // No data = deny
      if (!data) return false;

      // view permission requires page access
      if (op === 'view') return hasPageAccess(pageKey);

      // Default-deny: empty operations config means no record → no permissions
      const operations = data.operations;
      if (!operations || Object.keys(operations).length === 0) return false;

      const pageOps = operations[pageKey];
      if (!pageOps || typeof pageOps !== 'object') return false;

      // Explicitly true = allow; anything else = deny
      return pageOps[op] === true;
    },
    [data, hasPageAccess]
  );

  return (
    <PermissionContext.Provider
      value={{ loading, isBoss, hasPageAccess, hasOperation, refresh: fetchPermissions }}
    >
      {children}
    </PermissionContext.Provider>
  );
};
